import {Component, Input, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {MyCheckUsage} from '../../../services/ruleset.service';

/**
 * Hinweisblock für Lösch-Bestätigungen: nennt die PRÜFLÄUFE, die das zu löschende myCheck bzw.
 * die zu löschende Prüfregel verwenden, und welche davon dadurch zum Entwurf werden.
 *
 * <p>Die Richtung ist hier wichtig: gelöscht wird ein myCheck (ehemals „Prüfregelset"), betroffen
 * sind die Prüfläufe (ehemals „myChecks"), die es in ihrer Regelauswahl referenzieren. Die
 * automatisch erzeugten Prüfläufe der Direktstarts tauchen bewusst NICHT auf – sie werden
 * serverseitig archiviert (siehe {@code MyCheckReferenceService#archiveAutoDefinitionsFor}) und
 * wären für den Nutzer ein Eintrag, den er nie angelegt hat.
 *
 * Rendert nichts, solange es keine Verwendungen gibt – kann also bedingungslos in ein
 * Bestätigungs-Popup eingebettet werden.
 */
@Component({
  selector: 'app-rule-usage-warning',
  standalone: true,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (loading) {
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <fa-icon [icon]="faSpinnerThird" class="animate-spin"></fa-icon>
        Verwendung in Prüfläufen wird geprüft…
      </div>
    } @else if (usages.length > 0) {
      <div class="flex flex-col gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
        <div class="flex items-start gap-2">
          <fa-icon [icon]="faTriangleExclamation" class="text-yellow-700 text-sm shrink-0 mt-0.5"></fa-icon>
          <span class="text-xs font-medium text-yellow-900">
            Wird aktuell in {{ usages.length }} Prüflauf{{ usages.length === 1 ? '' : 'en' }} verwendet. Beim Löschen
            {{ usages.length === 1 ? 'wird die Zuordnung dort' : 'werden die Zuordnungen dort' }} entfernt.
          </span>
        </div>
        <ul class="flex flex-col gap-1 max-h-32 overflow-y-auto pl-6">
          @for (usage of usages; track usage.definitionId) {
            <li class="flex items-center gap-2 text-xs text-yellow-900">
              <span class="truncate">{{ usage.name || 'Unbenannter Prüflauf' }}</span>
              @if (!usage.editable) {
                <span class="shrink-0 px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 text-[10px] font-medium">
                  nicht änderbar
                </span>
              } @else if (usage.becomesDraft) {
                <span class="shrink-0 px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 text-[10px] font-medium">
                  wird Entwurf
                </span>
              }
            </li>
          }
        </ul>
        @if (hasBecomesDraft) {
          <span class="text-xs text-yellow-800 pl-6">
            Als „wird Entwurf" markierte Checks haben danach keine Prüfregel mehr und sind bis zur erneuten
            Konfiguration nicht ausführbar.
          </span>
        }
      </div>
    }
  `,
})
export class RuleUsageWarningComponent {
  @Input() usages: MyCheckUsage[] = [];
  @Input() loading = false;

  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTriangleExclamation = faTriangleExclamation;

  get hasBecomesDraft(): boolean {
    return this.usages.some(usage => usage.becomesDraft && usage.editable);
  }
}
